import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
  Typography,
  Alert,
} from '@mui/material'
import { Add, Edit, Delete, Visibility, Warning, Science, LocalHospital } from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, Modal } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const SOURCE_TYPES = [
  { value: 'sealed', label: 'Sealed Source' },
  { value: 'brachytherapy', label: 'Brachytherapy Source' },
  { value: 'calibration', label: 'Calibration Source' },
]

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'success' },
  { value: 'in_storage', label: 'In Storage', color: 'info' },
  { value: 'decayed', label: 'Decayed', color: 'warning' },
  { value: 'disposed', label: 'Disposed', color: 'default' },
  { value: 'transferred', label: 'Transferred', color: 'secondary' },
]

const COMMON_ISOTOPES = [
  'Ir-192',
  'Co-60',
  'Cs-137',
  'I-125',
  'Pd-103',
  'Sr-90',
  'Ra-226',
]

const RadiationSourcesList = () => {
  const [sources, setSources] = useState([])
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [leakTestOpen, setLeakTestOpen] = useState(false)
  const [selectedSource, setSelectedSource] = useState(null)
  const [saving, setSaving] = useState(false)
  const [inventorySummary, setInventorySummary] = useState(null)
  const [formData, setFormData] = useState({
    source_id: '',
    source_type: 'brachytherapy',
    isotope: '',
    manufacturer: '',
    model_number: '',
    serial_number: '',
    initial_activity_gbq: '',
    calibration_date: '',
    half_life_days: '',
    asset: '',
    storage_location: '',
    status: 'active',
    received_date: '',
    license_number: '',
    regulatory_body: '',
    notes: '',
  })
  const [leakTestData, setLeakTestData] = useState({
    result: '',
    test_date: '',
    next_test_date: '',
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const [sourcesRes, assetsRes, summaryRes] = await Promise.all([
        api.get('/maintenance/radiation-sources/'),
        api.get('/assets/assets/'),
        api.get('/maintenance/radiation-sources/inventory_summary/'),
      ])
      setSources(sourcesRes.data.results || sourcesRes.data)
      setAssets(assetsRes.data.results || assetsRes.data)
      setInventorySummary(summaryRes.data)
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load radiation sources')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedSource(null)
    const today = new Date().toISOString().split('T')[0]
    setFormData({
      source_id: '',
      source_type: 'brachytherapy',
      isotope: '',
      manufacturer: '',
      model_number: '',
      serial_number: '',
      initial_activity_gbq: '',
      calibration_date: today,
      half_life_days: '',
      asset: '',
      storage_location: '',
      status: 'active',
      received_date: today,
      license_number: '',
      regulatory_body: '',
      notes: '',
    })
    setFormOpen(true)
  }

  const handleEdit = (source) => {
    setSelectedSource(source)
    setFormData({
      source_id: source.source_id || '',
      source_type: source.source_type || 'brachytherapy',
      isotope: source.isotope || '',
      manufacturer: source.manufacturer || '',
      model_number: source.model_number || '',
      serial_number: source.serial_number || '',
      initial_activity_gbq: source.initial_activity_gbq || '',
      calibration_date: source.calibration_date || '',
      half_life_days: source.half_life_days || '',
      asset: source.asset || '',
      storage_location: source.storage_location || '',
      status: source.status || 'active',
      received_date: source.received_date || '',
      license_number: source.license_number || '',
      regulatory_body: source.regulatory_body || '',
      notes: source.notes || '',
    })
    setFormOpen(true)
  }

  const handleView = (source) => {
    setSelectedSource(source)
    setDetailOpen(true)
  }

  const handleLeakTest = (source) => {
    setSelectedSource(source)
    const today = new Date().toISOString().split('T')[0]
    const sixMonths = new Date()
    sixMonths.setMonth(sixMonths.getMonth() + 6)
    setLeakTestData({
      result: '',
      test_date: today,
      next_test_date: sixMonths.toISOString().split('T')[0],
    })
    setLeakTestOpen(true)
  }

  const handleSave = async () => {
    if (!formData.source_id || !formData.isotope || !formData.initial_activity_gbq) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      const payload = {
        ...formData,
        asset: formData.asset || null,
        initial_activity_gbq: parseFloat(formData.initial_activity_gbq),
        half_life_days: parseFloat(formData.half_life_days),
      }
      if (selectedSource) {
        await api.patch(`/maintenance/radiation-sources/${selectedSource.id}/`, payload)
        toast.success('Radiation source updated')
      } else {
        await api.post('/maintenance/radiation-sources/', payload)
        toast.success('Radiation source created')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving source:', error)
      toast.error(error.response?.data?.error || 'Failed to save radiation source')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveLeakTest = async () => {
    if (!leakTestData.result) {
      toast.error('Please enter test result')
      return
    }

    setSaving(true)
    try {
      await api.post(`/maintenance/radiation-sources/${selectedSource.id}/record_leak_test/`, leakTestData)
      toast.success('Leak test recorded')
      setLeakTestOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error recording leak test:', error)
      toast.error('Failed to record leak test')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (source) => {
    if (!window.confirm(`Delete radiation source ${source.source_id}?`)) return
    try {
      await api.delete(`/maintenance/radiation-sources/${source.id}/`)
      toast.success('Radiation source deleted')
      fetchData()
    } catch (error) {
      console.error('Error deleting source:', error)
      toast.error('Failed to delete radiation source')
    }
  }

  const getStatusColor = (status) => {
    const option = STATUS_OPTIONS.find((o) => o.value === status)
    return option?.color || 'default'
  }

  const columns = [
    { field: 'source_id', headerName: 'Source ID', sortable: true },
    { field: 'isotope', headerName: 'Isotope' },
    {
      field: 'source_type',
      headerName: 'Type',
      renderCell: (v) => (
        <Chip
          label={SOURCE_TYPES.find((t) => t.value === v)?.label || v}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'current_activity_gbq',
      headerName: 'Current Activity',
      renderCell: (v) => v ? `${v} GBq` : '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (v) => (
        <Chip
          label={STATUS_OPTIONS.find((o) => o.value === v)?.label || v}
          size="small"
          color={getStatusColor(v)}
        />
      ),
    },
    { field: 'storage_location', headerName: 'Location', renderCell: (v) => v || '-' },
    {
      field: 'next_leak_test_date',
      headerName: 'Next Leak Test',
      renderCell: (v) => {
        if (!v) return '-'
        const date = new Date(v)
        const today = new Date()
        const isOverdue = date < today
        return (
          <Box display="flex" alignItems="center" gap={1}>
            {date.toLocaleDateString()}
            {isOverdue && <Chip label="Overdue" size="small" color="error" />}
          </Box>
        )
      },
      sortable: true,
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleView(row) }}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => { e.stopPropagation(); handleLeakTest(row) }}
            title="Record Leak Test"
          >
            <Science fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row) }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  const activeSources = sources.filter((s) => s.status === 'active').length
  const leakTestsOverdue = inventorySummary?.leak_tests_overdue || 0

  return (
    <Box>
      <PageHeader
        title="Radiation Sources"
        subtitle="Brachytherapy and sealed source inventory management"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Radiation Oncology' },
          { label: 'Radiation Sources' },
        ]}
        actions={
          <Button startIcon={<Add />} onClick={handleAdd}>
            Add Source
          </Button>
        }
      />

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <LocalHospital sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700} color="primary">
                {activeSources}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Sources
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="info.main">
                {sources.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Inventory
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color={leakTestsOverdue > 0 ? 'error.main' : 'success.main'}>
                {leakTestsOverdue}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Leak Tests Overdue
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {leakTestsOverdue > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} icon={<Warning />}>
          {leakTestsOverdue} radiation source(s) have overdue leak tests - immediate action required
        </Alert>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={sources}
          loading={loading}
          searchPlaceholder="Search radiation sources..."
          emptyMessage="No radiation sources found"
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedSource ? 'Edit Radiation Source' : 'Add Radiation Source'}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {selectedSource ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Source ID"
              value={formData.source_id}
              onChange={(e) => setFormData({ ...formData, source_id: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Source Type</InputLabel>
              <Select
                value={formData.source_type}
                onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                label="Source Type"
              >
                {SOURCE_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Isotope</InputLabel>
              <Select
                value={formData.isotope}
                onChange={(e) => setFormData({ ...formData, isotope: e.target.value })}
                label="Isotope"
              >
                {COMMON_ISOTOPES.map((i) => (
                  <MenuItem key={i} value={i}>{i}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Status"
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Initial Activity (GBq)"
              type="number"
              value={formData.initial_activity_gbq}
              onChange={(e) => setFormData({ ...formData, initial_activity_gbq: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Calibration Date"
              type="date"
              value={formData.calibration_date}
              onChange={(e) => setFormData({ ...formData, calibration_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Half-Life (days)"
              type="number"
              value={formData.half_life_days}
              onChange={(e) => setFormData({ ...formData, half_life_days: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Manufacturer"
              value={formData.manufacturer}
              onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Serial Number"
              value={formData.serial_number}
              onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Storage Location"
              value={formData.storage_location}
              onChange={(e) => setFormData({ ...formData, storage_location: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Received Date"
              type="date"
              value={formData.received_date}
              onChange={(e) => setFormData({ ...formData, received_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="License Number"
              value={formData.license_number}
              onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Regulatory Body"
              value={formData.regulatory_body}
              onChange={(e) => setFormData({ ...formData, regulatory_body: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Associated Equipment</InputLabel>
              <Select
                value={formData.asset}
                onChange={(e) => setFormData({ ...formData, asset: e.target.value })}
                label="Associated Equipment"
              >
                <MenuItem value="">None</MenuItem>
                {assets.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Leak Test Modal */}
      <Modal
        open={leakTestOpen}
        onClose={() => setLeakTestOpen(false)}
        title="Record Leak Test"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setLeakTestOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveLeakTest} loading={saving}>Save</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Source: {selectedSource?.source_id} ({selectedSource?.isotope})
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Test Result"
              value={leakTestData.result}
              onChange={(e) => setLeakTestData({ ...leakTestData, result: e.target.value })}
              placeholder="e.g., Pass, Fail, <0.005 uCi"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Test Date"
              type="date"
              value={leakTestData.test_date}
              onChange={(e) => setLeakTestData({ ...leakTestData, test_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Next Test Date"
              type="date"
              value={leakTestData.next_test_date}
              onChange={(e) => setLeakTestData({ ...leakTestData, next_test_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Radiation Source Details"
        maxWidth="sm"
        actions={
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>Close</Button>
        }
      >
        {selectedSource && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Source ID</Box>
                <Box fontWeight={600}>{selectedSource.source_id}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Isotope</Box>
                <Box fontWeight={600}>{selectedSource.isotope}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Type</Box>
                <Chip
                  label={SOURCE_TYPES.find((t) => t.value === selectedSource.source_type)?.label}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Status</Box>
                <Chip
                  label={STATUS_OPTIONS.find((o) => o.value === selectedSource.status)?.label}
                  size="small"
                  color={getStatusColor(selectedSource.status)}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Initial Activity</Box>
                <Box fontWeight={600}>{selectedSource.initial_activity_gbq} GBq</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Current Activity</Box>
                <Box fontWeight={600} color="primary.main">
                  {selectedSource.current_activity_gbq} GBq
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Half-Life</Box>
                <Box fontWeight={600}>{selectedSource.half_life_days} days</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Calibration Date</Box>
                <Box fontWeight={600}>
                  {selectedSource.calibration_date
                    ? new Date(selectedSource.calibration_date).toLocaleDateString()
                    : '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Last Leak Test</Box>
                <Box fontWeight={600}>
                  {selectedSource.last_leak_test_date
                    ? new Date(selectedSource.last_leak_test_date).toLocaleDateString()
                    : '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Next Leak Test</Box>
                <Box fontWeight={600}>
                  {selectedSource.next_leak_test_date
                    ? new Date(selectedSource.next_leak_test_date).toLocaleDateString()
                    : '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Storage Location</Box>
                <Box fontWeight={600}>{selectedSource.storage_location || '-'}</Box>
              </Box>
            </Grid>
            {selectedSource.notes && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Notes</Box>
                  <Box mt={0.5}>{selectedSource.notes}</Box>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Modal>
    </Box>
  )
}

export default RadiationSourcesList
